import type { ReactNode } from 'react';
import { useSettings } from './settings-context';

// The console's line icons (admin design `icn()`, D:7832): 24-unit viewBox,
// 1.8 stroke, round caps, `currentColor`. Only the glyphs the console uses. The
// design's `taka` glyph is a hardcoded currency sign; here the `currency` icon
// draws the registry currency symbol instead (INF-1 tenant-blind).
export type NavIconName =
  | 'grid'
  | 'clipboard'
  | 'layers'
  | 'calendar'
  | 'tag'
  | 'scale'
  | 'star'
  | 'store'
  | 'user'
  | 'currency'
  | 'card'
  | 'swap'
  | 'chart'
  | 'layout'
  | 'folder'
  | 'pin'
  | 'pen'
  | 'bell'
  | 'shield'
  | 'search'
  | 'logout'
  | 'sun'
  | 'moon'
  | 'lock'
  | 'chat';

const PATHS: Record<Exclude<NavIconName, 'currency'>, ReactNode> = {
  grid: (
    <>
      <rect x="3" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" />
    </>
  ),
  clipboard: (
    <>
      <rect x="5" y="4.5" width="14" height="17" rx="2.5" />
      <path d="M9 4.5V3h6v1.5M8.5 10.5h7M8.5 14.5h5" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3l9 5-9 5-9-5 9-5z" />
      <path d="M3 13.5l9 5 9-5" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
      <path d="M8 3v4M16 3v4M3.5 10.5h17" />
    </>
  ),
  tag: (
    <>
      <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
      <circle cx="7.5" cy="7.5" r="1.1" fill="currentColor" stroke="none" />
    </>
  ),
  scale: (
    <>
      <path d="M12 4.5v14.5M8.5 19h7M4.5 7.5h15" />
      <path d="M6.5 7.5L4 13a2.7 2.7 0 0 0 5 0L6.5 7.5zM17.5 7.5L15 13a2.7 2.7 0 0 0 5 0l-2.5-5.5z" />
    </>
  ),
  star: <path d="M12 3.6l2.5 5.2 5.7.8-4.1 4 1 5.7-5.1-2.7-5.1 2.7 1-5.7-4.1-4 5.7-.8L12 3.6z" />,
  store: <path d="M4 9L5.4 4h13.2L20 9M4 9h16M5 9v11h14V9M9.5 20v-5.5h5V20" />,
  user: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
  card: (
    <>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 9.5h19M6 15h4" />
    </>
  ),
  swap: <path d="M4 8h14M15 5l3 3-3 3M20 16H6M9 13l-3 3 3 3" />,
  chart: <path d="M6.5 20v-7M12 20V5.5M17.5 20v-4.5M3.5 20h17" />,
  layout: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <path d="M3 9.5h18M9.5 21V9.5" />
    </>
  ),
  folder: <path d="M3.5 7a2 2 0 0 1 2-2h4L12 7.5h6.5a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V7z" />,
  pin: (
    <>
      <path d="M12 21s-7-5.6-7-11a7 7 0 0 1 14 0c0 5.4-7 11-7 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </>
  ),
  pen: (
    <>
      <path d="M4 20l1.2-4.2L16.5 4.5a2.1 2.1 0 0 1 3 3L8.2 18.8 4 20z" />
      <path d="M14.5 6.5l3 3" />
    </>
  ),
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3l7 3v5.2c0 4.3-2.9 7.3-7 8.8-4.1-1.5-7-4.5-7-8.8V6l7-3z" />
      <path d="M9.2 11.8l2 2 3.6-3.8" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M21 21l-4.3-4.3" />
    </>
  ),
  logout: <path d="M14.5 4H19a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4.5M10 8l-4 4 4 4M6 12h10" />,
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
    </>
  ),
  moon: <path d="M20 13.2A8 8 0 1 1 10.8 4a6.4 6.4 0 0 0 9.2 9.2z" />,
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10.5" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </>
  ),
  chat: (
    <>
      <path d="M21 11.5a8 8 0 0 1-8.5 8 8.6 8.6 0 0 1-3.6-.7L3.5 20l1.2-4.4A8 8 0 1 1 21 11.5z" />
      <path d="M8.5 10.5h7M8.5 13.5h4.5" />
    </>
  ),
};

function CurrencyGlyph() {
  const { settings } = useSettings();
  const symbol = settings?.currency?.symbol ?? settings?.currency?.code?.charAt(0) ?? '';
  return (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <text x="12" y="15.6" textAnchor="middle" fontSize="10.5" fontWeight="700" fill="currentColor" stroke="none">
        {symbol}
      </text>
    </>
  );
}

export function NavIcon({ name, size = 17.5 }: { name: NavIconName; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: '0 0 auto' }}
    >
      {name === 'currency' ? <CurrencyGlyph /> : PATHS[name]}
    </svg>
  );
}
